import {
  Box,
  useTheme,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  Tooltip,
  MenuItem,
} from "@mui/material";
import { useStateContext } from "contexts/ContextProvider";
import React, { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid2";

import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { OVERLAY_NAMES } from "constants/Overlays";
import api from "services/api";
import { useEditLock } from "hooks/useEditLock";
import { countryOptions } from "constants/countryData";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import EditLockDialog from "components/EditLockDialog";
import Overlay from "components/Overlay/Overlay";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import { parsePhoneNumber } from "libphonenumber-js";

// Helper: format a phone number based on country code (national format, e.g. (555) 555-1234)
const formatPhoneNumber = (raw, countryCode) => {
  if (!raw) return "";
  try {
    const phoneNumber = parsePhoneNumber(raw, countryCode);
    if (phoneNumber && phoneNumber.isValid()) {
      let formatted = phoneNumber.formatNational();
      const cc = String(phoneNumber.countryCallingCode || "");
      // Strip leading trunk prefix (0) for non-US numbers
      if (cc && cc !== "1" && formatted.startsWith("0")) {
        formatted = formatted.slice(1);
      }
      // Include country code prefix for non-US numbers
      if (cc && cc !== "1") {
        formatted = `+${cc} ${formatted}`;
      }
      return formatted;
    }
  } catch {
    // fall through
  }
  return raw;
};

const RequestorNewEdit = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const { overlayData, setOverlayName, triggerPageRefresh } = useStateContext();
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [isEditable, setIsEditable] = useState(true);

  const { currentUser } = useAuth();
  const { socket } = useWebSocket();

  const isNewRequestor =
    overlayData.Action === OVERLAY_NAMES.REQUESTOR_ACTION_NEW;

  // Edit lock for existing requestors only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "requestor",
    isNewRequestor ? null : overlayData.Requestor?.id,
  );

  const [name, setName] = useState(
    isNewRequestor ? "" : overlayData.Requestor.Name || "",
  );
  const initReqPhones = () => {
    if (
      !isNewRequestor &&
      overlayData.Requestor?.PhoneNumbers &&
      overlayData.Requestor.PhoneNumbers.length > 0
    ) {
      return overlayData.Requestor.PhoneNumbers.map((p, idx) => {
        const matchedCountry = countryOptions.find(
          (c) => String(c.callingCode) === String(p.CountryCode),
        );
        const countryCode = matchedCountry?.code || "US";
        const rawNumber = p.Number || "";
        return {
          id: p.id || `existing-${idx}`,
          countryCode,
          callingCode: String(p.CountryCode || "1"),
          number: rawNumber ? formatPhoneNumber(rawNumber, countryCode) : "",
          ext: p.Ext || "",
          label: p.Label || "",
          isPrimary: p.IsPrimary || false,
          sortOrder: p.SortOrder || idx,
        };
      });
    }
    return [
      {
        id: "new-0",
        countryCode: "US",
        callingCode: "1",
        number: "",
        ext: "",
        label: "",
        isPrimary: true,
        sortOrder: 0,
      },
    ];
  };

  const [phoneNumbers, setPhoneNumbers] = useState(initReqPhones);
  const [email, setEmail] = useState(
    isNewRequestor ? "" : overlayData.Requestor.Email || "",
  );

  const [isInvoicer, setIsInvoicer] = useState(
    isNewRequestor ? false : overlayData.Requestor.IsInvoicer,
  );
  const [isMain, setIsMain] = useState(
    overlayData.Action === OVERLAY_NAMES.REQUESTOR_ACTION_NEW
      ? false
      : overlayData.Requestor.IsMain,
  );

  const [nameError, setNameError] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState(false);
  const [emailError, setEmailError] = useState(false);

  const [shouldValidate, setShouldValidate] = useState(false);

  const validateFields = useCallback(() => {
    setNameError(!name || name.trim() === "");
    setPhoneNumberError(false);
    setEmailError(false);
  }, [name]);

  const addNewRequestor = useCallback(() => {
    setIsSavingChanges(true);
    setIsEditable(false);

    api
      .post("/clients/addNewRequestor", {
        data: {
          Name: name.trim(),
          PhoneNumber: phoneNumbers[0]?.number
            ? phoneNumbers[0].number.replace(/\D/g, "")
            : null,
          Ext: phoneNumbers[0]?.ext || null,
          Email: email ? email.trim() : "",
          IsMain: isMain,
          IsInvoicer: isInvoicer,
          ClientID: overlayData.ClientID,
          PhoneNumbers: phoneNumbers
            .filter((p) => p.number && p.number.trim())
            .map((p, idx) => {
              const rawNumber = p.number.replace(/\D/g, "");
              // Strip the calling code prefix from the number if present
              const callingCode = String(p.callingCode || "1");
              let cleanNumber = rawNumber;
              if (callingCode !== "1" && cleanNumber.startsWith(callingCode)) {
                cleanNumber = cleanNumber.slice(callingCode.length);
              } else if (callingCode === "1" && cleanNumber.length > 10 && cleanNumber.startsWith("1")) {
                cleanNumber = cleanNumber.slice(1);
              }
              return {
              ...(p.id && !String(p.id).startsWith("new-") ? { id: Number(p.id) || p.id } : {}),
                CountryCode: p.callingCode || "1",
                Number: cleanNumber,
                Ext: p.ext ? p.ext.trim() : null,
                Label: p.label ? p.label.trim() : null,
                IsPrimary: p.isPrimary || idx === 0,
                SortOrder: p.sortOrder || idx,
              };
            }),
        },
      })
      .then((response) => {
        setIsSavingChanges(false);

        const clientId = overlayData?.ClientID;
        const changeAction = "Created";
        if (socket && clientId) {
          socket.emit("entity:saved", {
            modelType: "Client",
            recordId: clientId,
            changeType: "Requestor",
            changeAction,
            user: {
              id: currentUser?.id,
              name: currentUser?.Name || "User",
              imageThumbnail: currentUser?.ImageThumbnail,
            },
          });
        }

        triggerPageRefresh("New Requestor Added Successfully!", theme);

        setOverlayName("");
      })
      .catch((err) => console.log(err));
  }, [
    currentUser?.ImageThumbnail,
    currentUser?.Name,
    currentUser?.id,
    email,
    isInvoicer,
    isMain,
    name,
    overlayData?.ClientID,
    phoneNumbers,
    setOverlayName,
    socket,
    theme,
    triggerPageRefresh,
  ]);

  const updateRequestor = useCallback(() => {
    setIsSavingChanges(true);
    setIsEditable(false);

    api
      .post("/clients/updateRequestor", {
        data: {
          RequestorID: overlayData.Requestor.id,
          Name: name.trim(),
          PhoneNumber: phoneNumbers[0]?.number
            ? phoneNumbers[0].number.replace(/\D/g, "")
            : null,
          Ext: phoneNumbers[0]?.ext || null,
          Email: email ? email.trim() : "",
          IsMain: isMain,
          IsInvoicer: isInvoicer,
          ClientID: overlayData.ClientID,
          PhoneNumbers: phoneNumbers
            .filter((p) => p.number && p.number.trim())
            .map((p, idx) => {
              const rawNumber = p.number.replace(/\D/g, "");
              // Strip the calling code prefix from the number if present
              const callingCode = String(p.callingCode || "1");
              let cleanNumber = rawNumber;
              if (callingCode !== "1" && cleanNumber.startsWith(callingCode)) {
                cleanNumber = cleanNumber.slice(callingCode.length);
              } else if (callingCode === "1" && cleanNumber.length > 10 && cleanNumber.startsWith("1")) {
                cleanNumber = cleanNumber.slice(1);
              }
              return {
                ...(p.id && !String(p.id).startsWith("new-") ? { id: Number(p.id) || p.id } : {}),
                CountryCode: p.callingCode || "1",
                Number: cleanNumber,
                Ext: p.ext ? p.ext.trim() : null,
                Label: p.label ? p.label.trim() : null,
                IsPrimary: p.isPrimary || idx === 0,
                SortOrder: p.sortOrder || idx,
              };
            }),
        },
      })
      .then((response) => {
        setIsSavingChanges(false);

        const clientId = overlayData?.ClientID;
        const changeAction = "Updated";
        if (socket && clientId) {
          socket.emit("entity:saved", {
            modelType: "Client",
            recordId: clientId,
            changeType: "Requestor",
            changeAction,
            user: {
              id: currentUser?.id,
              name: currentUser?.Name || "User",
              imageThumbnail: currentUser?.ImageThumbnail,
            },
          });
        }

        triggerPageRefresh("Requestor Updated Sucessfully", theme);

        setOverlayName("");
      })
      .catch((err) => console.log(err));
  }, [
    currentUser?.ImageThumbnail,
    currentUser?.Name,
    currentUser?.id,
    email,
    isInvoicer,
    isMain,
    name,
    overlayData?.ClientID,
    overlayData?.Requestor?.id,
    phoneNumbers,
    setOverlayName,
    socket,
    theme,
    triggerPageRefresh,
  ]);

  useEffect(() => {
    if (shouldValidate) {
      if (!nameError && !phoneNumberError && !emailError) {
        overlayData.Action === OVERLAY_NAMES.REQUESTOR_ACTION_NEW
          ? addNewRequestor()
          : updateRequestor();
      }
      setShouldValidate(false);
    }
  }, [
    addNewRequestor,
    emailError,
    nameError,
    overlayData.Action,
    phoneNumberError,
    shouldValidate,
    updateRequestor,
  ]);

  const saveRequestor = useCallback(() => {
    validateFields();
    setShouldValidate(true);
  }, [validateFields]);

  const attemptSave = useCallback(() => {
    if (isSavingChanges || isLocked) {
      return;
    }

    saveRequestor();
  }, [isLocked, isSavingChanges, saveRequestor]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const target = e.target;
      const tagName = target?.tagName;
      const isTextArea = tagName === "TEXTAREA";
      const isContentEditable = !!target?.isContentEditable;
      const isInsideDialog =
        target instanceof Element && !!target.closest('[role="dialog"]');

      if (
        e.key === "Enter" &&
        !e.shiftKey &&
        !isTextArea &&
        !isContentEditable &&
        !isInsideDialog
      ) {
        e.preventDefault();
        attemptSave();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [attemptSave]);

  const footer = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "end",
        alignItems: "center",
        width: "100%",
        flexDirection: "row",
        p: ".75rem 2rem",
      }}
    >
      {isMobile ? (
        <Tooltip title="Discard" placement="top">
          <IconButton
            color="primary"
            onClick={() => setOverlayName("")}
            sx={{
              width: "40px",
              height: "40px",
              border: `2px solid ${theme.palette.primary.main}`,
              borderRadius: "12px",
            }}
          >
            <DisabledByDefaultRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          color="primary"
          variant="outlined"
          size="large"
          endIcon={<DisabledByDefaultRoundedIcon />}
          onClick={() => setOverlayName("")}
          sx={{
            borderRadius: "12px",
          }}
        >
          Discard
        </Button>
      )}
      {isMobile ? (
        <Tooltip title="Save" placement="top">
          <IconButton
            color="primary"
            onClick={attemptSave}
            disabled={isSavingChanges || isLocked}
            sx={{
              width: "40px",
              height: "40px",
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.background.default,
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            <SaveRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          loading={isSavingChanges}
          disabled={isSavingChanges || isLocked}
          color="primary"
          variant="contained"
          size="large"
          endIcon={<SaveRoundedIcon />}
          type="submit"
          onClick={attemptSave}
          sx={{
            borderRadius: "12px",
          }}
        >
          Save
        </Button>
      )}
    </Box>
  );

  return (
    <>
      <Overlay
        id="requestor-new-edit"
        open={true}
        onClose={() => setOverlayName("")}
        title={
          overlayData.Action === OVERLAY_NAMES.REQUESTOR_ACTION_NEW
            ? "New Requestor"
            : "Edit Requestor"
        }
        footer={footer}
      >
        <Box
          component="form"
          sx={{
            display: "flex",
            p: "1rem 2.3rem 1rem 2.5rem",
            width: "100%",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          <Grid container spacing={2} width="100%">
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id={"Requestor_Name"}
                label="Name"
                error={nameError}
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setNameError(
                    !event.target.value || event.target.value.trim() === "",
                  );
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id={"Requestor_Email"}
                label="Email"
                error={emailError}
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: "none" }}>
              <FormControlLabel
                value="Main Requestor"
                control={
                  <Checkbox
                    disabled={!isEditable}
                    label="Main Requestor"
                    checked={isMain}
                    onChange={(change) => setIsMain(!isMain)}
                  />
                }
                label="Main Requestor"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: "none" }}>
              <FormControlLabel
                value="Invoice Requestor"
                control={
                  <Checkbox
                    disabled={!isEditable}
                    label="Invoice Requestor"
                    checked={isInvoicer}
                    onChange={(change) => setIsInvoicer(!isInvoicer)}
                  />
                }
                label="Invoice Requestor"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5, mt: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  Phone Numbers
                </Typography>
                <IconButton
                  disabled={!isEditable}
                  size="small"
                  onClick={() => {
                    setPhoneNumbers((prev) => [
                      ...prev,
                      {
                        id: `new-${Date.now()}`,
                        countryCode: prev[prev.length - 1]?.countryCode || "US",
                        callingCode: prev[prev.length - 1]?.callingCode || "1",
                        number: "",
                        ext: "",
                        label: "",
                        isPrimary: false,
                        sortOrder: prev.length,
                      },
                    ]);
                  }}
                >
                  <AddRoundedIcon fontSize="small" />
                </IconButton>
              </Box>
              {phoneNumbers.map((phone, index) => (
                <Grid
                  container
                  key={phone.id}
                  spacing={1}
                  sx={{ mb: index < phoneNumbers.length - 1 ? 2 : 0 }}
                >
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      select
                      label="Code"
                      value={phone.countryCode || "US"}
                      onChange={(event) => {
                        const selectedCode = event.target.value;
                        const country = countryOptions.find(
                          (c) => c.code === selectedCode,
                        );
                        setPhoneNumbers((prev) =>
                          prev.map((p, i) =>
                            i === index
                              ? {
                                  ...p,
                                  countryCode: selectedCode,
                                  callingCode: country
                                    ? String(country.callingCode)
                                    : p.callingCode,
                                }
                              : p,
                          ),
                        );
                      }}
                      fullWidth
                      SelectProps={{
                        renderValue: (selCode) => {
                          const c = countryOptions.find(
                            (o) => o.code === selCode,
                          );
                          if (!c) return selCode;
                          return (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <img
                                src={c.flagUrl}
                                alt={c.name}
                                style={{ width: 18, height: "auto", borderRadius: 2 }}
                              />
                              <span>{c.name}</span>
                              <span style={{ color: "#999", fontSize: 12 }}>+{c.callingCode}</span>
                            </span>
                          );
                        },
                        MenuProps: {
                          PaperProps: {
                            sx: { maxHeight: 350 },
                          },
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& .MuiInputBase-input": {
                            padding: "8.5px 12px !important",
                          },
                        },
                      }}
                    >
                      {countryOptions.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                          <Box
                            component="span"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              width: "100%",
                            }}
                          >
                            <img
                              src={country.flagUrl}
                              alt={country.name}
                              style={{ width: 24, height: "auto", borderRadius: 2, flexShrink: 0 }}
                              loading="lazy"
                            />
                            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {country.name}
                            </span>
                            <span style={{ color: "#999", fontSize: 13, flexShrink: 0 }}>
                              +{country.callingCode}
                            </span>
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      label="Phone Number"
                      error={phoneNumberError && index === 0}
                      value={
                        phone._phoneRaw !== undefined
                          ? phone._phoneRaw
                          : phone.number || ""
                      }
                      onChange={(event) => {
                        let raw = event.target.value;
                        const hasPlus = raw.trimStart().startsWith("+");
                        const digits = raw.replace(/\D/g, "");
                        raw = hasPlus ? "+" + digits : digits;
                        setPhoneNumbers((prev) =>
                          prev.map((p, i) =>
                            i === index ? { ...p, number: raw, _phoneRaw: raw } : p,
                          ),
                        );
                        if (index === 0) {
                          setPhoneNumberError(raw.trim() === "");
                        }
                      }}
                      onFocus={() => {
                        setPhoneNumbers((prev) =>
                          prev.map((p, i) => {
                            if (i !== index) return p;
                            const hasPlus = p.number.trimStart().startsWith("+");
                            const digits = p.number.replace(/\D/g, "");
                            const clean = hasPlus ? "+" + digits : digits;
                            return { ...p, _phoneRaw: clean };
                          }),
                        );
                      }}
                      onBlur={() => {
                        setPhoneNumbers((prev) =>
                          prev.map((p, i) => {
                            if (i !== index) return p;
                            const formatted = formatPhoneNumber(
                              p.number,
                              p.countryCode || "US",
                            );
                            return { ...p, number: formatted, _phoneRaw: undefined };
                          }),
                        );
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 2 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      label="Ext"
                      value={phone.ext || ""}
                      onChange={(event) => {
                        const val = event.target.value;
                        setPhoneNumbers((prev) =>
                          prev.map((p, i) =>
                            i === index ? { ...p, ext: val } : p,
                          ),
                        );
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 2 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      label="Label"
                      placeholder="e.g. Mobile"
                      value={phone.label || ""}
                      onChange={(event) => {
                        const val = event.target.value;
                        setPhoneNumbers((prev) =>
                          prev.map((p, i) =>
                            i === index ? { ...p, label: val } : p,
                          ),
                        );
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid
                    size={{ xs: 12, sm: 2 }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: { xs: "flex-end", sm: "center" },
                      gap: 0.25,
                    }}
                  >
                    <Tooltip
                      title={
                        phone.isPrimary ? "Primary phone" : "Set as primary"
                      }
                    >
                      <span>
                        <IconButton
                          disabled={!isEditable}
                          size="small"
                          onClick={() => {
                            setPhoneNumbers((prev) =>
                              prev.map((p, i) => ({
                                ...p,
                                isPrimary: i === index,
                              })),
                            );
                          }}
                        >
                          {phone.isPrimary ? (
                            <StarRoundedIcon
                              fontSize="small"
                              sx={{ color: "warning.main" }}
                            />
                          ) : (
                            <StarBorderRoundedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <IconButton
                      disabled={!isEditable || index === 0}
                      size="small"
                      onClick={() => {
                        setPhoneNumbers((prev) => {
                          const next = [...prev];
                          [next[index - 1], next[index]] = [
                            next[index],
                            next[index - 1],
                          ];
                          return next.map((p, i) => ({ ...p, sortOrder: i }));
                        });
                      }}
                    >
                      <ArrowUpwardRoundedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      disabled={!isEditable || index === phoneNumbers.length - 1}
                      size="small"
                      onClick={() => {
                        setPhoneNumbers((prev) => {
                          const next = [...prev];
                          [next[index + 1], next[index]] = [
                            next[index],
                            next[index + 1],
                          ];
                          return next.map((p, i) => ({ ...p, sortOrder: i }));
                        });
                      }}
                    >
                      <ArrowDownwardRoundedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      disabled={!isEditable || phoneNumbers.length <= 1}
                      size="small"
                      onClick={() => {
                        setPhoneNumbers((prev) => {
                          const next = prev.filter((_, i) => i !== index);
                          if (phone.isPrimary && next.length > 0) {
                            next[0].isPrimary = true;
                          }
                          return next;
                        });
                      }}
                    >
                      <RemoveRoundedIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Box>
      </Overlay>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </>
  );
};

export default RequestorNewEdit;
